<script setup lang="ts">
import axios from 'axios';
import { computed, ref, watch } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import DropZone from '@/components/Tools/DropZone.vue';
import FileCard from '@/components/Tools/FileCard.vue';
import PdfPageReorderGrid from '@/components/Tools/PdfPageReorderGrid.vue';
import StatusMessage from '@/components/Tools/StatusMessage.vue';
import ToolHowToCard from '@/components/Tools/ToolHowToCard.vue';
import ToolLayout from '@/components/Tools/ToolLayout.vue';
import { Button } from '@/components/ui/button';
import { usePdfUploadLimit } from '@/composables/usePdfUploadLimit';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import { dashboard } from '@/routes';
import { reorder as reorderPage } from '@/routes/tools/pdf';
import reorderTool from '@/routes/tools/pdf/reorder';

const { lineWithMax } = usePdfUploadLimit();
const { t } = useTranslations();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.pdf.reorder.seo',
    fallbackTitleKey: 'tools.pdf.reorder.title',
    fallbackTitleLabel: 'Reorder pages',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: reorderPage.url(),
});

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
            {
                translationKey: 'tools.pdf.reorder.title',
                title: 'Reorder pages',
                href: reorderPage.url(),
            },
        ],
    },
});

type Status = 'idle' | 'loading' | 'success' | 'error';

const file = ref<File | null>(null);
const pageOrder = ref<number[]>([]);
const gridReady = ref(false);
const status = ref<Status>('idle');
const statusMessage = ref('');

const fileKey = computed(() =>
    file.value
        ? `${file.value.name}-${file.value.size}-${file.value.lastModified}`
        : '',
);

watch(file, (f) => {
    if (!f) {
        pageOrder.value = [];
        gridReady.value = false;
    } else {
        gridReady.value = false;
    }
});

function onFilesSelected(files: File[]): void {
    file.value = files[0] ?? null;
    status.value = 'idle';
    gridReady.value = false;

    if (!file.value) {
        pageOrder.value = [];

        return;
    }

    pageOrder.value = [];
}

function onGridReady(): void {
    gridReady.value = true;
}

function onGridError(): void {
    gridReady.value = false;
}

function removeFile(): void {
    file.value = null;
    pageOrder.value = [];
    gridReady.value = false;
    status.value = 'idle';
}

async function extractError(err: unknown): Promise<string> {
    const e = err as { response?: { data: Blob | object } };

    if (e.response?.data instanceof Blob) {
        const text = await (e.response.data as Blob).text();

        try {
            const parsed = JSON.parse(text);
            const messages = Object.values(parsed.errors ?? {}).flat();

            return (
                (messages[0] as string) ||
                t('tools.pdf.ui.reorder_failed', 'Reorder failed.')
            );
        } catch {
            return t('tools.pdf.ui.reorder_failed', 'Reorder failed.');
        }
    }

    return t('tools.pdf.ui.reorder_failed', 'Reorder failed.');
}

async function reorder(): Promise<void> {
    if (
        !file.value ||
        !gridReady.value ||
        pageOrder.value.length === 0 ||
        status.value === 'loading'
    ) {
        return;
    }

    const order = pageOrder.value.join(',');

    status.value = 'loading';
    statusMessage.value = t('tools.pdf.ui.reordering', 'Reordering pages...');

    const formData = new FormData();
    formData.append('file', file.value);
    formData.append('order', order);

    try {
        const response = await axios.post(reorderTool.submit.url(), formData, {
            responseType: 'blob',
        });

        const cd = (response.headers['content-disposition'] as string) ?? '';
        const match = cd.match(/filename="?([^"]+)"?/);
        const filename = match?.[1] ?? 'reordered.pdf';

        const blobUrl = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.rel = 'noopener';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        window.setTimeout(() => {
            a.remove();
            URL.revokeObjectURL(blobUrl);
        }, 1500);

        status.value = 'success';
        statusMessage.value = t(
            'tools.pdf.ui.reordered_success',
            'Pages reordered successfully!',
        );
        file.value = null;
        pageOrder.value = [];
        gridReady.value = false;
    } catch (err: unknown) {
        status.value = 'error';
        statusMessage.value = await extractError(err);
    }
}
</script>

<template>
    <ToolPageHead v-bind="seo" json-ld-head-key="ld-json-pdf-reorder" />

    <ToolLayout
        :title="t('tools.pdf.reorder.title', 'Reorder pages')"
        :subtitle="t('tools.pdf.reorder.description', '')"
        :show-how-to="false"
    >
        <div
            class="grid gap-3 sm:gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,17rem)] lg:items-start lg:gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,19rem)]"
        >
            <!-- Left: page order grid (wider column) -->
            <div
                class="space-y-3 sm:space-y-4 lg:sticky lg:top-4 lg:min-w-0 lg:self-start"
            >
                <PdfPageReorderGrid
                    v-if="file"
                    :key="fileKey"
                    v-model="pageOrder"
                    :file="file"
                    :show-header-download="false"
                    :submitting="status === 'loading'"
                    @ready="onGridReady"
                    @error="onGridError"
                    @submit="reorder"
                />
                <div
                    v-else
                    class="flex min-h-56 items-center justify-center rounded-xl border border-border bg-card p-4 text-center text-sm text-muted-foreground shadow-sm sm:min-h-72 sm:p-6"
                >
                    {{
                        t(
                            'tools.reorder_grid.empty_state',
                            'Upload a PDF to arrange pages here.',
                        )
                    }}
                </div>
                <ToolHowToCard />
            </div>

            <!-- Right: compact upload + primary download (same pattern as merge / rotate) -->
            <div class="space-y-3 sm:space-y-4">
                <div
                    class="rounded-xl border border-border bg-card p-3 shadow-sm sm:p-4"
                >
                    <DropZone
                        accept="application/pdf"
                        :multiple="false"
                        :label="
                            t(
                                'tools.pdf.ui.drop_pdf_here',
                                'Click or drag PDF here',
                            )
                        "
                        :sublabel="
                            lineWithMax(
                                t(
                                    'tools.pdf.ui.pdf_files_only',
                                    'PDF files only',
                                ),
                            )
                        "
                        @update:model-value="onFilesSelected"
                    />

                    <div v-if="file" class="mt-3">
                        <FileCard
                            :file="file"
                            :show-drag-handle="false"
                            @remove="removeFile"
                        />
                    </div>
                </div>

                <Button
                    type="button"
                    class="min-h-11 w-full touch-manipulation bg-brand-600 text-white hover:bg-brand-700"
                    :disabled="
                        !file ||
                        !gridReady ||
                        pageOrder.length === 0 ||
                        status === 'loading'
                    "
                    @click="reorder"
                >
                    {{ t('tools.reorder_grid.download', 'Download PDF') }}
                </Button>

                <StatusMessage :status="status" :message="statusMessage" />
            </div>
        </div>
    </ToolLayout>
</template>
